import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, Plus, RefreshCw, UserCheck } from "lucide-react";
import { apiError, recruitersAdminApi } from "../api";
import { dayLong, lineTitle, money, PRODUCER_STATUS, RECRUITER_STATUS, STAGE } from "../format";
import { Badge, CommissionBreakdownView, CommissionStatusBadge, Empty, KeyValue, SectionTitle, Stat, Tabs, type TabDef } from "../ui";
import { EventCard } from "../EventCard";
import type { AdminRecruiterDetail, RecruiterEventRow } from "../types";
import { StatementRow, PayModal, ObserveInvoiceModal } from "./StatementsPane";
import { AdjustmentModal, AssignProducerModal, EditRecruiterModal, RedateProducerModal, ReleaseProducerModal, ReviewModal } from "./detailModals";
import { LoadingBlock, ErrorBlock } from "../../dashboard/shared/StateBlocks";
import { useToast } from "@/app/providers/Toaster";
import { Can, usePermission } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";
import type { Statement } from "../types";

type Tab = "resumen" | "cartera" | "eventos" | "comisiones" | "liquidaciones";

export function RecruiterDetailScreen() {
  const { id } = useParams();
  const rid = Number(id);
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab) || "resumen";
  const q = useQuery({ queryKey: ["recruiters", "detail", rid], queryFn: () => recruitersAdminApi.get(rid), enabled: Number.isFinite(rid) });

  if (q.isLoading) return <LoadingBlock label="Cargando reclutador…" />;
  if (q.isError || !q.data) return <ErrorBlock onRetry={() => q.refetch()} />;
  const d = q.data;
  const toReview = d.events.filter((e) => e.review.outcome === "pending" && !!e.review.lastSubmittedOn).length;
  const tabs: TabDef<Tab>[] = [
    { id: "resumen", label: "Resumen" },
    { id: "cartera", label: "Cartera", count: d.producers.filter((p) => p.status === "proposed").length || undefined },
    { id: "eventos", label: "Eventos", count: toReview || undefined },
    { id: "comisiones", label: "Comisiones" },
    { id: "liquidaciones", label: "Liquidaciones" },
  ];

  return (
    <div className="space-y-4">
      <Link to="/reclutadores" className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft size={14} /> Reclutadores
      </Link>
      <Header d={d} />
      <Tabs tabs={tabs} active={tab} onChange={(t) => setParams(t === "resumen" ? {} : { tab: t })} />
      {tab === "resumen" && <SummaryTab d={d} />}
      {tab === "cartera" && <PortfolioTab d={d} />}
      {tab === "eventos" && <EventsTab d={d} />}
      {tab === "comisiones" && <CommissionsTab d={d} />}
      {tab === "liquidaciones" && <StatementsTab d={d} />}
    </div>
  );
}

function Header({ d }: { d: AdminRecruiterDetail }) {
  const { push } = useToast();
  const qc = useQueryClient();
  const r = d.recruiter;
  const st = RECRUITER_STATUS[r.status];
  const [editing, setEditing] = useState(false);
  const sync = useMutation({
    mutationFn: () => recruitersAdminApi.sync(r.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recruiters"] });
      push({ kind: "success", message: "Recalculado con los datos actuales." });
    },
    onError: (err) => push({ kind: "error", message: apiError(err) }),
  });
  const welcome = useMutation({
    mutationFn: () => recruitersAdminApi.resendWelcome(r.id),
    onSuccess: () => push({ kind: "success", message: `Le reenviamos la bienvenida a ${r.email}.` }),
    onError: (err) => push({ kind: "error", message: apiError(err) }),
  });
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="nx-h1 truncate">{r.name}</h1>
          <Badge tone={st.tone}>{st.label}</Badge>
          {!r.userLinked && <Badge tone="warning">Aún no entra a Nexus</Badge>}
        </div>
        <p className="text-sm text-fg-muted mt-1">
          {r.email}
          {r.phone ? ` · ${r.phone}` : ""}
        </p>
      </div>
      <Can permission={PERMISSIONS.RECRUITERS_MANAGE}>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="nx-btn-ghost" onClick={() => sync.mutate()} disabled={sync.isPending}>
            {sync.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Recalcular
          </button>
          <button type="button" className="nx-btn-ghost" onClick={() => welcome.mutate()} disabled={welcome.isPending}>
            <Mail size={14} /> Reenviar bienvenida
          </button>
          <button type="button" className="nx-btn-secondary" onClick={() => setEditing(true)}>
            Editar
          </button>
        </div>
      </Can>
      {editing && <EditRecruiterModal recruiter={r} onClose={() => setEditing(false)} />}
    </div>
  );
}

function SummaryTab({ d }: { d: AdminRecruiterDetail }) {
  const r = d.recruiter;
  const canPay = usePermission(PERMISSIONS.RECRUITERS_PAY);
  const totals = useMemo(() => {
    const t = { estimated: 0, pending: 0, payable: 0, inStatement: 0, paid: 0 };
    for (const c of d.commissions) {
      if (c.status === "estimated") t.estimated += c.amount;
      else if (c.status === "pending_checklist") t.pending += c.amount;
      else if (c.status === "payable") t.payable += c.amount;
      else if (c.status === "in_statement") t.inStatement += c.amount;
      else if (c.status === "paid") t.paid += c.amount;
    }
    return t;
  }, [d.commissions]);
  const active = d.producers.filter((p) => p.status === "active").length;
  const pipeline = d.producers.filter((p) => p.status === "pipeline").length;
  const upcoming = d.events.filter((e) => e.date >= d.today && !e.canceled).length;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Productores activos" value={active} note={`${pipeline} en pipeline`} />
        <Stat label="Próximos eventos" value={upcoming} />
        <Stat label="Por pagar" value={money(totals.payable + totals.inStatement)} tone="accent" note={`Estimado aparte: ${money(totals.estimated + totals.pending)}`} />
        <Stat label="Pagado" value={money(totals.paid)} tone="success" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="nx-card p-4 space-y-3">
          <SectionTitle>Datos</SectionTitle>
          <KeyValue
            rows={[
              ["RUT", r.rut || "—"],
              ["Acuerdo", `${r.agreementVersion || "—"}${r.agreementSignedOn ? ` · firmado el ${dayLong(r.agreementSignedOn)}` : ""}`],
              ["En el programa desde", dayLong(r.createdAt.slice(0, 10))],
              ...(r.endedOn ? ([["Término", `${dayLong(r.endedOn)}${r.endReason ? ` · ${r.endReason}` : ""}`]] as Array<[string, string]>) : []),
              ["Notas internas", r.internalNotes || "—"],
            ]}
          />
        </section>
        <section className="nx-card p-4 space-y-3">
          <SectionTitle>Datos para pagarle</SectionTitle>
          {canPay && r.bank ? (
            r.bank.complete ? (
              <KeyValue
                rows={[
                  ["Banco", r.bank.bankName],
                  ["Cuenta", `${r.bank.accountType} n.º ${r.bank.accountNumber}`],
                  ["Titular", `${r.bank.holderName} · ${r.bank.holderRut}`],
                ]}
              />
            ) : (
              <p className="text-sm text-warning">Aún no completa sus datos bancarios en "Mis datos".</p>
            )
          ) : (
            <p className="text-sm text-fg-muted">Sólo los ve quien gestiona los pagos.</p>
          )}
        </section>
      </div>
    </div>
  );
}

function PortfolioTab({ d }: { d: AdminRecruiterDetail }) {
  const [assigning, setAssigning] = useState(false);
  const [releasing, setReleasing] = useState<number | null>(null);
  const [redating, setRedating] = useState<number | null>(null);
  const groups = [
    { title: "Activos", rows: d.producers.filter((p) => p.status === "active") },
    { title: "En pipeline", rows: d.producers.filter((p) => p.status === "pipeline") },
    { title: "Propuestos (revísalos en Reclutadores → Propuestas)", rows: d.producers.filter((p) => p.status === "proposed") },
    { title: "Historial", rows: d.producers.filter((p) => ["expired", "rejected", "released"].includes(p.status)) },
  ];
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Can permission={PERMISSIONS.RECRUITERS_MANAGE}>
          <button type="button" className="nx-btn-primary" disabled={d.recruiter.status === "ended"} onClick={() => setAssigning(true)}>
            <Plus size={14} /> Asignar productor del CRM
          </button>
        </Can>
      </div>
      {d.producers.length === 0 && <Empty>Sin productores en su cartera todavía.</Empty>}
      {groups
        .filter((g) => g.rows.length)
        .map((g) => (
          <section key={g.title} className="space-y-2">
            <h3 className="text-sm font-semibold text-fg-muted">{g.title}</h3>
            <ul className="grid gap-2">
              {g.rows.map((p) => {
                const st = PRODUCER_STATUS[p.status];
                return (
                  <li key={p.id} className="nx-card p-4 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-fg">{p.name}</span>
                        <Badge tone={st.tone}>{st.label}</Badge>
                        {!p.producerLinked && p.status !== "proposed" && <Badge tone="neutral">Sin cuenta enlazada</Badge>}
                      </div>
                      <div className="text-xs text-fg-muted mt-1 space-y-0.5">
                        {p.registeredOn && <div>Atribución desde el {dayLong(p.registeredOn)}</div>}
                        {p.activatedOn && <div>Activado el {dayLong(p.activatedOn)} · {p.events.counted} evento{p.events.counted === 1 ? "" : "s"} con ventas · {STAGE[p.events.stage]}</div>}
                        {p.status === "pipeline" && p.attribution?.expiresOn && (
                          <div className={p.attribution.daysLeft !== null && p.attribution.daysLeft <= 14 ? "text-warning" : ""}>
                            Caduca el {dayLong(p.attribution.expiresOn)} si no hay actividad ({p.attribution.daysLeft} días)
                          </div>
                        )}
                        {p.events.next && <div>Próximo: {p.events.next.name} · {dayLong(p.events.next.date)}</div>}
                        {p.decisionNote && <div>Nota de Mixcore: {p.decisionNote}</div>}
                      </div>
                    </div>
                    {(p.status === "pipeline" || p.status === "active") && (
                      <Can permission={PERMISSIONS.RECRUITERS_MANAGE}>
                        <div className="flex flex-wrap gap-1">
                          <button type="button" className="nx-btn-ghost" onClick={() => setRedating(p.id)}>
                            Cambiar fecha
                          </button>
                          <button type="button" className="nx-btn-ghost text-danger" onClick={() => setReleasing(p.id)}>
                            Liberar
                          </button>
                        </div>
                      </Can>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      {assigning && <AssignProducerModal recruiterId={d.recruiter.id} recruiterName={d.recruiter.name} onClose={() => setAssigning(false)} />}
      {releasing && <ReleaseProducerModal producer={d.producers.find((p) => p.id === releasing)!} onClose={() => setReleasing(null)} />}
      {redating && <RedateProducerModal producer={d.producers.find((p) => p.id === redating)!} onClose={() => setRedating(null)} />}
    </div>
  );
}

function EventsTab({ d }: { d: AdminRecruiterDetail }) {
  const [reviewing, setReviewing] = useState<{ ev: RecruiterEventRow; action: "approve" | "observe" | "retain" | "reopen" } | null>(null);
  const upcoming = d.events.filter((e) => e.date >= d.today);
  const past = d.events.filter((e) => e.date < d.today).reverse();
  const actions = (ev: RecruiterEventRow) => {
    if (ev.canceled) return null;
    const inStatement = ev.commission?.status === "in_statement" || ev.commission?.status === "paid";
    const s = ev.review.status;
    const cureOver = s === "observed" && !!ev.review.cureDeadline && d.today > ev.review.cureDeadline;
    return (
      <Can permission={PERMISSIONS.RECRUITERS_MANAGE}>
        <div className="flex flex-wrap gap-2 pt-1 border-t border-border">
          {!inStatement && s !== "approved" && (
            <button type="button" className="nx-btn-primary" onClick={() => setReviewing({ ev, action: "approve" })}>
              <UserCheck size={14} /> Aprobar checklist
            </button>
          )}
          {!inStatement && s !== "observed" && s !== "retained" && (
            <button type="button" className="nx-btn-secondary" onClick={() => setReviewing({ ev, action: "observe" })}>
              Observar un ítem
            </button>
          )}
          {cureOver && (
            <button type="button" className="nx-btn-danger" onClick={() => setReviewing({ ev, action: "retain" })}>
              Retener comisión
            </button>
          )}
          {!inStatement && (s === "approved" || s === "retained") && (
            <button type="button" className="nx-btn-ghost" onClick={() => setReviewing({ ev, action: "reopen" })}>
              Reabrir revisión
            </button>
          )}
        </div>
      </Can>
    );
  };
  return (
    <div className="space-y-5">
      {d.events.length === 0 && <Empty>Sus productores aún no tienen eventos desde que se los asignaron.</Empty>}
      {upcoming.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-fg-muted">Próximos</h3>
          {upcoming.map((ev) => (
            <EventCard key={ev.id} ev={ev} today={d.today} footer={actions(ev)} />
          ))}
        </section>
      )}
      {past.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-fg-muted">Realizados</h3>
          {past.map((ev) => (
            <EventCard key={ev.id} ev={ev} today={d.today} footer={actions(ev)} defaultOpen={ev.review.outcome === "pending" && !!ev.review.lastSubmittedOn} />
          ))}
        </section>
      )}
      {reviewing && <ReviewModal ev={reviewing.ev} action={reviewing.action} onClose={() => setReviewing(null)} />}
    </div>
  );
}

function CommissionsTab({ d }: { d: AdminRecruiterDetail }) {
  const { push } = useToast();
  const qc = useQueryClient();
  const [adjusting, setAdjusting] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const voidAdj = useMutation({
    mutationFn: (cid: number) => recruitersAdminApi.voidAdjustment(cid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recruiters"] });
      push({ kind: "success", message: "Ajuste anulado." });
    },
    onError: (err) => push({ kind: "error", message: apiError(err) }),
  });
  const rows = [...d.commissions].filter((c) => c.status !== "void").reverse();
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Can permission={PERMISSIONS.RECRUITERS_PAY}>
          <button type="button" className="nx-btn-secondary" onClick={() => setAdjusting(true)}>
            <Plus size={14} /> Ajuste manual
          </button>
        </Can>
      </div>
      {rows.length === 0 && <Empty>Aún no tiene comisiones.</Empty>}
      <ul className="grid gap-2">
        {rows.map((c) => (
          <li key={c.id} className="nx-card">
            <button type="button" className="w-full text-left p-4 flex flex-wrap items-center justify-between gap-3" onClick={() => setOpen(open === c.id ? null : c.id)}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-fg">{lineTitle(c)}</div>
                <div className="text-xs text-fg-muted">{c.producerName ?? ""}{c.reconciledOn ? ` · conciliada el ${dayLong(c.reconciledOn)}` : ""}</div>
              </div>
              <div className="flex items-center gap-3">
                <CommissionStatusBadge status={c.status} />
                <span className="text-sm font-semibold tabular-nums text-fg">{money(c.amount)}</span>
              </div>
            </button>
            {open === c.id && (
              <div className="px-4 pb-4 space-y-2">
                <CommissionBreakdownView line={c} />
                {c.kind === "adjustment" && !c.statementId && (
                  <Can permission={PERMISSIONS.RECRUITERS_PAY}>
                    <button type="button" className="nx-btn-ghost text-danger" onClick={() => voidAdj.mutate(c.id)}>
                      Anular ajuste
                    </button>
                  </Can>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {adjusting && <AdjustmentModal recruiterId={d.recruiter.id} onClose={() => setAdjusting(false)} />}
    </div>
  );
}

function StatementsTab({ d }: { d: AdminRecruiterDetail }) {
  const [paying, setPaying] = useState<Statement | null>(null);
  const [observing, setObserving] = useState<Statement | null>(null);
  if (d.statements.length === 0) {
    return <Empty>Aún no tiene liquidaciones. Se generan cada mes en Reclutadores → Liquidaciones.</Empty>;
  }
  return (
    <>
      <ul className="grid gap-3">
        {d.statements.map((s) => (
          <StatementRow key={s.id} s={{ ...s, recruiterName: d.recruiter.name }} hideName onPay={() => setPaying(s)} onObserve={() => setObserving(s)} />
        ))}
      </ul>
      {paying && <PayModal s={{ ...paying, recruiterName: d.recruiter.name }} onClose={() => setPaying(null)} />}
      {observing && <ObserveInvoiceModal s={observing} onClose={() => setObserving(null)} />}
    </>
  );
}
